import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type {
  ColDef,
  CellValueChangedEvent,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Timestamp } from 'firebase/firestore';

import type { ActionGridRow, DropdownConfig, ActionStatus, Feasibility, RowStatus } from '@/types';
import { useAppStore } from '@/store/appStore';
import { Loader2, AlertCircle, Maximize2, Minimize2, Plus, Trash2, X, AlertTriangle, Eye, Edit, Save, RotateCcw, Database } from 'lucide-react';
import { createAction, deleteActions, getClientsByCountry, moveAction, renameClient } from '@/services/firestoreService';
import { exportFirestoreData, exportFirestoreDataAsCSV } from '@/services/exportService';
import { useAuth } from '@/hooks/useAuth';
// import { ClientCellEditor } from './ClientCellEditor'; // Removed unused import
import { MapSelectCellEditor } from './MapSelectCellEditor';
import { MaterialCellEditor } from './MaterialCellEditor';
import { AddClientModal } from './AddClientModal';
import { MoveActionsModal } from './MoveActionsModal';
import { RenameClientModal } from './RenameClientModal';

interface ActionGridProps {
  data: ActionGridRow[];
  countryId: string;
  countryIsoCode?: string;
  clientId: string;
  dropdownConfig: DropdownConfig;
  onDataChange?: () => void;
  onUnsavedChangesChange?: (hasChanges: boolean) => void;
  onClientCreated?: (client: { id: string, name: string }) => void;
  showDeleted?: boolean;
  showInnovationTeam?: boolean;
  onSectionChange?: (section: 'commercial' | 'innovation' | 'mitigation') => void;
  collectionRoot?: 'data' | 'mitigation';
}

type ViewMode = 'compact' | 'comfortable' | 'auto';

// Future toggle:
// Set to true when managers should be able to edit every locked cell in Commercial Actions.
// This is intentionally disabled for now so current behavior does not change.
const ENABLE_MANAGER_FULL_GRID_EDIT = false;

// Hidden for now at the user's request; keep the toggle + logic intact in case it's needed again.
const ENABLE_VIEW_MODE_TOGGLE = false;

export function ActionGrid({
  data,
  countryId,
  countryIsoCode,
  clientId,
  dropdownConfig,
  onDataChange,
  onUnsavedChangesChange,
  onClientCreated,
  showDeleted = false,
  showInnovationTeam = true,
  onSectionChange,
  collectionRoot = 'data',
}: ActionGridProps) {
  const gridRef = useRef<AgGridReact>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('comfortable');
  const [selectedRows, setSelectedRows] = useState<ActionGridRow[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<ActionGridRow> & { originalClientId?: string }>>({});
  const [modifiedRows, setModifiedRows] = useState<Set<string>>(new Set());
  const [clients, setClients] = useState<{ id: string, name: string }[]>([]);

  const { setError } = useAppStore();
  const { user, isManager } = useAuth();
  const canEditGrid = user?.role !== 'innovation++';
  const managerCanEditLockedCells =
    ENABLE_MANAGER_FULL_GRID_EDIT && isManager && isEditMode;

  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  const fetchClients = useCallback(async () => {
    if (!countryId) return;
    try {
      const clientsMap = await getClientsByCountry(countryId);
      const clientsList = Object.values(clientsMap).map(c => ({ id: c.id, name: c.name }));
      setClients(clientsList);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, [countryId]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleClientAdded = useCallback((newClient: { id: string, name: string }) => {
    fetchClients();
    if (onClientCreated) {
      onClientCreated(newClient);
    }
  }, [fetchClients, onClientCreated]);

  useEffect(() => {
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(Object.keys(pendingChanges).length > 0);
    }
  }, [pendingChanges, onUnsavedChangesChange]);

  useEffect(() => {
    if (canEditGrid) return;
    if (isEditMode) setIsEditMode(false);
  }, [canEditGrid, isEditMode]);

  const normalizeDropdown = useCallback((configValue: any): { map: Record<string, string>, keys: string[] } => {
    if (!configValue) return { map: {}, keys: [] };
    if (Array.isArray(configValue)) {
      const map: Record<string, string> = {};
      const sortedKeys = [...configValue].sort((a, b) => a.localeCompare(b));
      sortedKeys.forEach(val => { map[val] = val; });
      return { map, keys: sortedKeys };
    }
    if (typeof configValue === 'object') {
      const keys = Object.keys(configValue).sort((a, b) => {
        const valA = configValue[a] || a;
        const valB = configValue[b] || b;
        return String(valA).localeCompare(String(valB));
      });
      return {
        map: configValue,
        keys
      };
    }
    return { map: {}, keys: [] };
  }, []);

  const isColumnEditable = useCallback((isStandardEditable: boolean = true) => {
    return isStandardEditable ? isEditMode : managerCanEditLockedCells;
  }, [isEditMode, managerCanEditLockedCells]);

  const getEditableCellClass = useCallback((
    baseClass: string | ((params: any) => string) = '',
    isEditable: boolean = true,
    preserveBackgroundWhenEditable: boolean = false
  ) => {
    return (params: any) => {
      let base = typeof baseClass === 'function' ? baseClass(params) : baseClass;
      const field = params.colDef?.field;
      const preserveStatusColors = field === 'feasibility' || field === 'status';
      const isCurrentlyEditable = isEditable ? isEditMode : managerCanEditLockedCells;
      if (isCurrentlyEditable) {
        const editableClass = preserveBackgroundWhenEditable ? 'editable-cell-preserve-bg' : 'editable-cell';
        return base ? `${base} ${editableClass}` : editableClass;
      }
      if (isEditMode && !isEditable && !preserveStatusColors) {
        return base ? `${base} locked-cell` : 'locked-cell';
      }
      return base;
    };
  }, [isEditMode, managerCanEditLockedCells]);

  const getRowClass = useCallback((params: any) => {
    if (params.data.rowStatus === 'Eliminated') {
      return 'bg-red-100 text-red-900';
    }
    if (!isEditMode) {
      return 'readonly-row';
    }
    if (modifiedRows.has(params.data.id)) {
      return 'modified-row';
    }
    return '';
  }, [modifiedRows, isEditMode]);

  const dropdownOptions = useMemo(() => ({
    typeOfAction: normalizeDropdown(dropdownConfig.typeOfAction),
    vcp: normalizeDropdown(dropdownConfig.vcp),
    timing: normalizeDropdown(dropdownConfig.timing),
    feasibility: normalizeDropdown(dropdownConfig.feasibility),
    status: normalizeDropdown(dropdownConfig.status),
    brand: normalizeDropdown(dropdownConfig.brand),
    familyRep: normalizeDropdown(dropdownConfig.familyRep),
  }), [dropdownConfig, normalizeDropdown]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.refreshCells({ force: true });
      gridRef.current.api.redrawRows();
    }
  }, [dropdownOptions]);

  const columnDefs = useMemo<ColDef[]>(() => {
    const cols: ColDef[] = [
    {
      headerName: '#',
      field: 'rowNumber',
      width: 80,
      minWidth: 60,
      maxWidth: 100,
      pinned: 'left',
      editable: isColumnEditable(false),
      cellClass: () => {
        if (managerCanEditLockedCells) {
          return 'font-bold text-gray-500 editable-cell';
        }
        if (isEditMode) {
          return 'locked-cell-strong font-bold text-[#eb4a4a]';
        }
        return 'font-bold text-gray-500';
      },
      sort: 'asc',
      checkboxSelection: true,
      headerCheckboxSelection: true,
    },
    {
      headerName: 'ID',
      field: 'id',
      width: 100,
      minWidth: 80,
      maxWidth: 150,
      pinned: 'left',
      editable: false,
      hide: true,
      cellClass: 'bg-gray-50 font-medium text-gray-600 text-xs',
    },
    {
      headerName: 'SAP Code',
      field: 'sapCode',
      width: 140,
      minWidth: 120,
      editable: isColumnEditable(false),
      valueFormatter: (params) => params.value || 'Pending',
      cellClass: getEditableCellClass(
        (params) => (!params.value ? 'text-gray-400 italic' : 'font-medium'),
        false
      ),
    },
    {
      headerName: 'Client',
      field: 'client',
      width: 180,
      minWidth: 150,
      pinned: 'left',
      editable: isColumnEditable(false),
      cellClass: getEditableCellClass('font-medium', false),
    },
    {
      headerName: 'Plant',
      field: 'plant',
      width: 150,
      minWidth: 120,
      editable: isColumnEditable(true),
      cellClass: getEditableCellClass(''),
    },
    {
      headerName: 'Date Introduced',
      field: 'dateIntroduced',
      width: 150,
      minWidth: 130,
      editable: isColumnEditable(false),
      cellEditor: 'agDateCellEditor',
      cellClass: getEditableCellClass('', false),
      valueFormatter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString();
      },
    },
    {
      headerName: 'Date Finalized',
      field: 'dateFinalized',
      width: 150,
      minWidth: 130,
      editable: isColumnEditable(false),
      cellEditor: 'agDateCellEditor',
      cellClass: getEditableCellClass('', false),
      valueFormatter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString();
      },
    },
    {
      headerName: 'Type of Action',
      field: 'typeOfAction',
      width: 180,
      minWidth: 150,
      editable: isColumnEditable(true),
      cellEditor: MapSelectCellEditor,
      cellEditorParams: {
        options: dropdownOptions.typeOfAction.map,
      },
      refData: dropdownOptions.typeOfAction.map,
      valueFormatter: (params) => dropdownOptions.typeOfAction.map[params.value] || params.value,
      filterValueGetter: (params) => dropdownOptions.typeOfAction.map[params.data?.typeOfAction] || params.data?.typeOfAction,
      cellClass: getEditableCellClass(),
    },
    {
      headerName: 'VCP Concept',
      field: 'vcpConcept',
      width: 250,
      minWidth: 200,
      editable: isColumnEditable(true),
      wrapText: true,
      autoHeight: true,
      cellEditor: MapSelectCellEditor,
      cellEditorParams: {
        options: dropdownOptions.vcp.map,
      },
      cellClass: getEditableCellClass(""),
      refData: dropdownOptions.vcp.map,
      valueFormatter: (params) => dropdownOptions.vcp.map[params.value] || params.value,
      filterValueGetter: (params) => dropdownOptions.vcp.map[params.data?.vcpConcept] || params.data?.vcpConcept,
    },
    {
      headerName: 'Volume/Year (Tn)',
      field: 'volumeYearTn',
      width: 160,
      minWidth: 140,
      editable: isColumnEditable(true),
      type: 'numericColumn',
      cellEditor: 'agNumberCellEditor',
      valueFormatter: (params) => {
        if (params.value == null) return '';
        return params.value.toLocaleString('es-ES', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
      cellClass: getEditableCellClass(""),
    },
    {
      headerName: 'GM II (€/kg)',
      field: 'gmIIEurKg',
      width: 140,
      minWidth: 120,
      editable: isColumnEditable(true),
      type: 'numericColumn',
      cellEditor: 'agNumberCellEditor',
      valueFormatter: (params) => {
        if (params.value == null) return '';
        return params.value.toLocaleString('es-ES', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + ' €';
      },
      cellClass: getEditableCellClass(''),
    },
    {
      headerName: 'Cost',
      field: 'cost',
      width: 140,
      minWidth: 120,
      editable: isColumnEditable(true),
      type: 'numericColumn',
      cellEditor: 'agNumberCellEditor',
      valueFormatter: (params) => {
        if (params.value == null) return '';
        return params.value.toLocaleString('es-ES', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + ' €';
      },
      cellClass: getEditableCellClass(''),
    },
    {
      headerName: 'Annual Expected Impact (€)',
      field: 'annualExpectedImpact',
      width: 200,
      minWidth: 180,
      editable: isColumnEditable(false),
      type: 'numericColumn',
      valueGetter: (params) => {
        if (managerCanEditLockedCells && params.data?.annualExpectedImpact != null) {
          return params.data.annualExpectedImpact;
        }
        const vol = params.data?.volumeYearTn || 0;
        const gm = params.data?.gmIIEurKg || 0;
        const cost = params.data?.cost || 0;
        return (vol * gm * 1000) - cost;
      },
      cellEditor: 'agNumberCellEditor',
      valueFormatter: (params) => {
        if (params.value == null) return '';
        return params.value.toLocaleString('es-ES', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }) + ' €';
      },
      cellClass: getEditableCellClass('font-bold', false),
    },
    {
      headerName: 'Achieved Annual Impact (€)',
      field: 'achievedAnnualImpact',
      width: 200,
      minWidth: 180,
      editable: isColumnEditable(false),
      type: 'numericColumn',
      valueGetter: (params) => {
        if (managerCanEditLockedCells && params.data?.achievedAnnualImpact != null) {
          return params.data.achievedAnnualImpact;
        }
        const vol = params.data?.volumeYearTn || 0;
        const gm = params.data?.gmIIEurKg || 0;
        const cost = params.data?.cost || 0;
        const annualExpectedImpact = (vol * gm * 1000) - cost;

        const statusVal = params.data?.status;
        const statusLabel = dropdownOptions.status.map[statusVal] || statusVal;

        const isCS = statusVal === 'CS' || statusLabel === 'CS';
        const isCNS = statusVal === 'CNS' || statusLabel === 'CNS';

        if (isCS || (isCNS && annualExpectedImpact < 0)) {
          return annualExpectedImpact;
        }
        return null;
      },
      cellEditor: 'agNumberCellEditor',
      valueFormatter: (params) => {
        if (params.value == null) return '';
        return params.value.toLocaleString('es-ES', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }) + ' €';
      },
      cellClass: getEditableCellClass('font-bold', false),
    },
    {
      headerName: 'Timing',
      field: 'timing',
      width: 130,
      minWidth: 110,
      editable: isColumnEditable(true),
      cellEditor: MapSelectCellEditor,
      cellEditorParams: {
        options: dropdownOptions.timing.map,
      },
      refData: dropdownOptions.timing.map,
      valueFormatter: (params) => dropdownOptions.timing.map[params.value] || params.value,
      filterValueGetter: (params) => dropdownOptions.timing.map[params.data?.timing] || params.data?.timing,
      cellClass: getEditableCellClass(""),
    },
    {
      headerName: 'Feasibility',
      field: 'feasibility',
      width: 130,
      minWidth: 110,
      editable: isColumnEditable(true),
      cellEditor: MapSelectCellEditor,
      cellEditorParams: {
        options: dropdownOptions.feasibility.map,
      },
      refData: dropdownOptions.feasibility.map,
      valueFormatter: (params) => dropdownOptions.feasibility.map[params.value] || params.value,
      filterValueGetter: (params) => dropdownOptions.feasibility.map[params.data?.feasibility] || params.data?.feasibility,
      cellClass: (params) => {
        const baseClass = getEditableCellClass('', true, true)(params);
        const val = params.value;
        const label = dropdownOptions.feasibility.map[val] || val;

        if (val === 'H' || label === 'High') return `${baseClass} bg-green-100 font-medium text-green-800`;
        if (val === 'L' || label === 'Low') return `${baseClass} bg-red-100 font-medium text-red-800`;
        if (val === 'M' || label === 'Medium') return `${baseClass} bg-yellow-100 font-medium text-yellow-800`;
        return baseClass;
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 140,
      minWidth: 120,
      editable: isColumnEditable(true),
      cellEditor: MapSelectCellEditor,
      cellEditorParams: {
        options: dropdownOptions.status.map,
      },
      refData: dropdownOptions.status.map,
      valueFormatter: (params) => dropdownOptions.status.map[params.value] || params.value,
      filterValueGetter: (params) => dropdownOptions.status.map[params.data?.status] || params.data?.status,
      cellClass: (params) => {
        const baseClass = getEditableCellClass('', true, true)(params);
        const val = params.value;
        const label = dropdownOptions.status.map[val] || val;

        const statusClasses: Record<string, string> = {
          'CS': 'bg-green-100 font-medium text-green-800',
          'CNS': 'bg-red-100 font-medium text-red-800',
          'WCTD': 'bg-blue-100 font-medium text-blue-800',
          'WIP': 'bg-yellow-100 font-medium text-yellow-800',
          'Not Started': 'bg-gray-100 font-medium text-gray-800',
        };

        const cssClass = statusClasses[val] || statusClasses[label] || '';
        return cssClass + ' ' + baseClass;
      },
    },
    {
      headerName: 'PIC/Owner',
      field: 'picOwner',
      width: 160,
      minWidth: 140,
      editable: isColumnEditable(true),
      cellClass: getEditableCellClass(""),
    },
    {
      headerName: 'Country',
      field: 'country',
      width: 130,
      minWidth: 110,
      editable: isColumnEditable(false),
      cellClass: getEditableCellClass('', false),
    },
    {
      headerName: 'Brand',
      field: 'brand',
      width: 150,
      minWidth: 120,
      editable: isColumnEditable(true),
      cellEditor: MapSelectCellEditor,
      cellEditorParams: {
        options: dropdownOptions.brand.map,
      },
      refData: dropdownOptions.brand.map,
      valueFormatter: (params) => dropdownOptions.brand.map[params.value] || params.value,
      filterValueGetter: (params) => dropdownOptions.brand.map[params.data?.brand] || params.data?.brand,
      cellClass: getEditableCellClass(""),
    },
    {
      headerName: 'Family Rep',
      field: 'familyRep',
      width: 160,
      minWidth: 140,
      editable: isColumnEditable(true),
      cellEditor: MapSelectCellEditor,
      cellEditorParams: {
        options: dropdownOptions.familyRep.map,
      },
      refData: dropdownOptions.familyRep.map,
      valueFormatter: (params) => dropdownOptions.familyRep.map[params.value] || params.value,
      filterValueGetter: (params) => dropdownOptions.familyRep.map[params.data?.familyRep] || params.data?.familyRep,
      cellClass: getEditableCellClass(""),
    },
    {
      headerName: 'Cod. SAP Material',
      field: 'codSapMaterial',
      width: 170,
      minWidth: 150,
      editable: isColumnEditable(true),
      cellEditor: MaterialCellEditor,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return String(params.value).replace(/^0+/, '');
      },
      cellClass: getEditableCellClass(""),
    },
    {
      headerName: 'Comments',
      field: 'notes',
      width: 300,
      minWidth: 200,
      editable: isColumnEditable(true),
      wrapText: true,
      autoHeight: true,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      cellEditorParams: {
        maxLength: 255,
      },
      cellClass: getEditableCellClass(""),
    },
    ];

    return cols;
  }, [
    dropdownOptions,
    isEditMode,
    clients,
    getEditableCellClass,
    countryId,
    countryIsoCode,
    isColumnEditable,
    managerCanEditLockedCells,
  ]);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  }), []);

  const onGridReady = useCallback(() => { }, []);
  const applyViewMode = useCallback((mode: ViewMode) => {
    if (!gridRef.current) return;
    const api = gridRef.current.api;
    switch (mode) {
      case 'compact': api.sizeColumnsToFit(); break;
      case 'comfortable':
        const allColumnIds = api.getColumns()?.map(col => col.getColId()) || [];
        api.autoSizeColumns(allColumnIds, false);
        break;
      case 'auto':
        const columnIds = api.getColumns()?.map(col => col.getColId()) || [];
        api.autoSizeColumns(columnIds, true);
        break;
    }
  }, []);
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    applyViewMode(mode);
  }, [applyViewMode]);
  // const autoSizeAll = useCallback(() => {
  //   if (!gridRef.current) return;
  //   const allColumnIds = gridRef.current.api.getColumns()?.map(col => col.getColId()) || [];
  //   gridRef.current.api.autoSizeColumns(allColumnIds, false);
  // }, []);

  const onCellValueChanged = useCallback((event: CellValueChangedEvent) => {
    if (!canEditGrid) return;
    const { data, colDef, newValue, oldValue } = event;
    if (newValue === oldValue) return;
    const editedField = colDef.field || '';
    const isManualManagerOverride =
      managerCanEditLockedCells &&
      (editedField === 'annualExpectedImpact' || editedField === 'achievedAnnualImpact');

    // --- LOGICA DE ACTUALIZACIÓN DE ESTADO PARA SAP ---
    // Al editar, cambiamos el estado a 'Updated', incluso si era 'Created'.
    const newRowStatus: RowStatus = 'Updated';
    // Actualizamos el objeto local data para que futuras ediciones sepan el estado
    data.rowStatus = newRowStatus;

    // Objeto para acumular todos los cambios de esta edición
    const changesToCommit: Partial<ActionGridRow> = {
      [colDef.field!]: newValue,
      rowStatus: newRowStatus,
    };

    // --- CÁLCULOS AUTOMÁTICOS DE IMPACTO ---
    // Recalculamos los impactos en CADA edición para asegurar que se guarden en Firestore
    // incluso si el usuario edita un campo no relacionado (ej: Notas), para corregir datos antiguos.

    const vol = colDef.field === 'volumeYearTn' ? newValue : (data.volumeYearTn || 0);
    const gm = colDef.field === 'gmIIEurKg' ? newValue : (data.gmIIEurKg || 0);
    const cost = colDef.field === 'cost' ? newValue : (data.cost || 0);
    const status = colDef.field === 'status' ? newValue : (data.status || 'Not Started');

    // 1. Annual Expected Impact - Round to 2 decimal places to avoid floating point errors
    if (!isManualManagerOverride) {
      const annualExpectedImpact = Math.round(((vol * gm * 1000) - cost) * 100) / 100;
      data.annualExpectedImpact = annualExpectedImpact; // Actualizar local
      changesToCommit.annualExpectedImpact = annualExpectedImpact;

      // 2. Achieved Annual Impact - Also rounded
      const isCS = status === 'CS' || dropdownOptions.status.map[status] === 'CS';
      const isCNS = status === 'CNS' || dropdownOptions.status.map[status] === 'CNS';

      let achievedAnnualImpact = 0;
      if (isCS) {
        achievedAnnualImpact = annualExpectedImpact;
      } else if (isCNS && annualExpectedImpact < 0) {
        achievedAnnualImpact = annualExpectedImpact;
      }

      data.achievedAnnualImpact = achievedAnnualImpact; // Actualizar local
      changesToCommit.achievedAnnualImpact = achievedAnnualImpact;
    }

    // --- LÓGICA DE STATUS 'CS' -> DATE FINALIZED ---
    if (colDef.field === 'status' && (newValue === 'CS' || dropdownOptions.status.map[newValue] === 'CS') && oldValue !== newValue) {
      const dateFinalized = new Date();
      data.dateFinalized = dateFinalized;
      changesToCommit.dateFinalized = dateFinalized;
    }

    // --- ACTUALIZAR PENDING CHANGES DE UNA SOLA VEZ ---
    setPendingChanges(prev => {
      const actionId = data.id;
      const currentChanges = prev[actionId] || {};

      return {
        ...prev,
        [actionId]: {
          ...currentChanges,
          ...changesToCommit
        }
      };
    });

    setModifiedRows(prev => {
      const newSet = new Set(prev);
      newSet.add(data.id);
      return newSet;
    });
  }, [dropdownOptions, managerCanEditLockedCells, canEditGrid]);

  const saveAllChanges = useCallback(async () => {
    if (Object.keys(pendingChanges).length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { doc, serverTimestamp, writeBatch } = await import('firebase/firestore');
      const { db } = await import('@/config/firebase');
      const { auth } = await import('@/config/firebase');
      const { getRegionIdForCountry } = await import('@/services/firestoreService');
      const regionId = await getRegionIdForCountry(countryId);
      if (!regionId) throw new Error(`No se pudo obtener regionId para el país: ${countryId}`);
      const batch = writeBatch(db);
      let updateCount = 0;
      for (const [actionId, changes] of Object.entries(pendingChanges)) {
        const originalRow = data.find(row => row.id === actionId);
        const targetClientId = originalRow?.clientId || clientId;
        if (targetClientId === 'all') continue;
        const actionRef = doc(db, collectionRoot, regionId, 'countries', countryId, 'clients', targetClientId, 'actions', actionId);

        // Aquí 'changes' ya incluye rowStatus si se modificó en onCellValueChanged
        const updateData = {
          ...changes,
          syncedToSAP: false, // Forzamos desincronización
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || 'unknown',
        };
        batch.update(actionRef, updateData);
        updateCount++;
      }
      await batch.commit();
      setPendingChanges({});
      setModifiedRows(new Set());
      if (onDataChange) onDataChange();
    } catch (error: any) {
      setSaveError(error.message || 'Error al guardar los cambios');
      setError(error.message);
    } finally {
      setSaving(false);
    }
  }, [pendingChanges, countryId, clientId, onDataChange, setError, data, collectionRoot]);

  const onSelectionChanged = useCallback(() => {
    if (gridRef.current) {
      const selectedNodes = gridRef.current.api.getSelectedNodes();
      const selectedData = selectedNodes.map(node => node.data) as ActionGridRow[];
      setSelectedRows(selectedData);
    }
  }, []);

  const handleExportFirestoreData = useCallback(async () => {
    try {
      setSaving(true);
      setSaveError(null);
      await exportFirestoreData();
    } catch (error: any) {
      setSaveError(error.message || 'Error al exportar los datos');
      setError(error.message);
    } finally {
      setSaving(false);
    }
  }, [setError]);

  const handleExportFirestoreDataAsCSV = useCallback(async () => {
    try {
      setSaving(true);
      setSaveError(null);
      await exportFirestoreDataAsCSV(collectionRoot);
    } catch (error: any) {
      setSaveError(error.message || 'Error al exportar los datos');
      setError(error.message);
    } finally {
      setSaving(false);
    }
  }, [setError, collectionRoot]);

  const handleAddRow = useCallback(async () => {
    if (!canEditGrid) {
      setError('Read-only access. Editing is disabled for your role.');
      return;
    }
    if (clientId === 'all') {
      setError('Please select a specific client to add a new row.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const maxRowNumber = data.reduce((max, row) => Math.max(max, row.rowNumber || 0), 0);
      const nextRowNumber = maxRowNumber + 1;

      const currentClient = clients.find(c => c.id === clientId);
      const clientName = currentClient ? currentClient.name : clientId;

      const newActionData = {
        rowNumber: nextRowNumber,
        country: countryId,
        client: clientName,
        sapCode: clientId,
        status: 'Not Started' as ActionStatus,
        feasibility: 'Medium' as Feasibility,
        dateIntroduced: Timestamp.fromDate(new Date()),
      };
      // createAction YA asigna rowStatus = 'Created' internamente en firestoreService
      await createAction(countryId, clientId, newActionData, collectionRoot);

      setIsEditMode(true);

      if (onDataChange) onDataChange();
    } catch (error: any) {
      setSaveError(error.message || 'Error al crear la acción');
      setError(error.message);
    } finally {
      setSaving(false);
    }
  }, [canEditGrid, data, countryId, clientId, onDataChange, setError, clients, collectionRoot]);

  const handleDeleteRows = useCallback(async () => {
    if (!canEditGrid) {
      setError('Read-only access. Editing is disabled for your role.');
      return;
    }
    if (selectedRows.length === 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      const rowsByClient: Record<string, string[]> = {};
      selectedRows.forEach(row => {
        const targetClient = row.clientId || clientId;
        if (targetClient === 'all') return;
        if (!rowsByClient[targetClient]) rowsByClient[targetClient] = [];
        rowsByClient[targetClient].push(row.id);
      });
      // deleteActions YA realiza el Soft Delete (rowStatus = 'Eliminated') en firestoreService
      await Promise.all(Object.entries(rowsByClient).map(([client, ids]) => deleteActions(countryId, client, ids, collectionRoot)));
      setIsDeleteModalOpen(false);
      setSelectedRows([]);
      if (onDataChange) onDataChange();
    } catch (error: any) {
      setSaveError(error.message || 'Error al eliminar las acciones');
      setError(error.message);
    } finally {
      setSaving(false);
    }
  }, [canEditGrid, selectedRows, countryId, clientId, onDataChange, setError, collectionRoot]);

  const handleRecoverRows = useCallback(async () => {
    if (!canEditGrid) {
      setError('Read-only access. Editing is disabled for your role.');
      return;
    }
    if (selectedRows.length === 0) return;

    // Filtrar solo las filas eliminadas
    const deletedRows = selectedRows.filter(row => row.rowStatus === 'Eliminated');
    if (deletedRows.length === 0) return;

    setPendingChanges(prev => {
      const newChanges = { ...prev };
      deletedRows.forEach(row => {
        newChanges[row.id] = {
          ...newChanges[row.id],
          rowStatus: 'Created', // Cambiar estado a Created
        };
      });
      return newChanges;
    });

    setModifiedRows(prev => {
      const newSet = new Set(prev);
      deletedRows.forEach(row => newSet.add(row.id));
      return newSet;
    });

    // Actualizar datos locales para reflejar el cambio inmediatamente en el grid
    deletedRows.forEach(row => {
      row.rowStatus = 'Created';
    });

    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.refreshCells({ force: true });
      gridRef.current.api.redrawRows();
    }

    setSelectedRows([]);
  }, [canEditGrid, selectedRows, setError]);

  const handleConfirmMoveActions = useCallback(async (targetClientId: string) => {
    if (selectedRows.length === 0) return;

    // Iteramos sobre las acciones seleccionadas para moverlas una por una
    // Podríamos optimizar un batch move en el servicio si fuera necesario, pero por ahora loop es seguro
    for (const row of selectedRows) {
      const sourceClientId = row.clientId || clientId;

      // Skip si el destino es el misom
      if (sourceClientId === targetClientId) continue;

      // Preparamos datos, excluyendo IDs y metadatos que generará el nuevo
      const { id, rowNumber, clientId: oldCId, client: oldCName, ...dataToSave } = row;

      // Buscar info del target client
      const targetClientObj = clients.find(c => c.id === targetClientId);
      const targetClientName = targetClientObj ? targetClientObj.name : targetClientId;

      // Convertir Dates a Timestamps
      const finalData: any = {
        ...dataToSave,
        client: targetClientName, // Nombre correcto del nuevo cliente
        sapCode: targetClientId,  // ID del nuevo cliente (que se usa como SAP Code en esta app)
      };

      if (finalData.dateIntroduced instanceof Date) {
        finalData.dateIntroduced = Timestamp.fromDate(finalData.dateIntroduced);
      }
      if (finalData.dateFinalized instanceof Date) {
        finalData.dateFinalized = Timestamp.fromDate(finalData.dateFinalized);
      }

      await moveAction(countryId, sourceClientId, targetClientId, finalData, row.id, collectionRoot);
    }

    // Refrescar datos
    if (onDataChange) onDataChange();
    setSelectedRows([]);
    setIsMoveModalOpen(false);
  }, [selectedRows, clientId, countryId, onDataChange, collectionRoot]);

  const handleConfirmRenameClient = useCallback(async (newName: string) => {
    if (clientId === 'all') return;

    await renameClient(countryId, clientId, newName);

    // Recargar clientes y datos
    await fetchClients();
    if (onDataChange) onDataChange();
    setIsRenameModalOpen(false);
  }, [clientId, countryId, fetchClients, onDataChange]);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 bg-white border-b border-gray-100">
        <div className="flex items-center gap-4">
          <div className="flex border border-gray-200 rounded-md overflow-hidden shadow-sm">
            <button
              type="button"
              onClick={() => onSectionChange?.('commercial')}
              className={`px-4 py-1.5 text-sm font-semibold transition-colors ${collectionRoot === 'data' ? 'bg-[#eb4a4a] text-white' : 'bg-white text-[#454240] hover:bg-gray-50'}`}
              aria-pressed={collectionRoot === 'data'}
            >
              Commercial Actions
            </button>
            {showInnovationTeam && (
              <button
                type="button"
                onClick={() => onSectionChange?.('innovation')}
                className="px-4 py-1.5 text-sm font-semibold transition-colors border-l border-gray-200 bg-white text-[#454240] hover:bg-gray-50"
                aria-pressed={false}
              >
                Innovation Team
              </button>
            )}
            <button
              type="button"
              onClick={() => onSectionChange?.('mitigation')}
              className={`px-4 py-1.5 text-sm font-semibold transition-colors border-l border-gray-200 ${collectionRoot === 'mitigation' ? 'bg-[#eb4a4a] text-white' : 'bg-white text-[#454240] hover:bg-gray-50'}`}
              aria-pressed={collectionRoot === 'mitigation'}
            >
              Mitigation
            </button>
          </div>
          {saving && (
            <div className="flex items-center gap-2 text-sm text-[#eb4a4a]">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Saving...</span>
            </div>
          )}
          {saveError && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4" />
              <span>{saveError}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {Object.keys(pendingChanges).length > 0 && (
            <button onClick={saveAllChanges} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-[#eb4a4a] rounded-md hover:bg-[#d93a3a] disabled:opacity-50 disabled:cursor-not-allowed animate-in fade-in slide-in-from-right-4 shadow-sm transition-all">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes ({Object.keys(pendingChanges).length})
            </button>
          )}

          {isManager && selectedRows.length > 0 && !showDeleted && (
            <button
              onClick={() => setIsMoveModalOpen(true)}
              className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-all shadow-sm animate-in fade-in slide-in-from-right-4"
            >
              <RotateCcw className="w-4 h-4" /> {/* Icono provisional, podría ser ArrowRight */}
              <span>Move Selected ({selectedRows.length})</span>
            </button>
          )}

          {isManager && clientId !== 'all' && isEditMode && (
            <button
              onClick={() => setIsRenameModalOpen(true)}
              className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 transition-all shadow-sm animate-in fade-in"
              title="Rename this client"
            >
              <Edit className="w-4 h-4" />
              <span>Rename Client</span>
            </button>
          )}

          {canEditGrid && (
            <button onClick={() => setIsEditMode(!isEditMode)} className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors shadow-sm ${isEditMode ? 'bg-[#454240] text-white hover:bg-[#333]' : 'bg-white text-[#454240] border border-[#e5e7eb] hover:bg-gray-50'}`} title={isEditMode ? 'Switch to View Mode' : 'Switch to Edit Mode'}>
              {isEditMode ? (<><Eye className="w-4 h-4" /><span>View Mode</span></>) : (<><Edit className="w-4 h-4" /><span>Edit Mode</span></>)}
            </button>
          )}

          {ENABLE_VIEW_MODE_TOGGLE && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">View:</span>
              <div className="flex border border-gray-200 rounded-md overflow-hidden shadow-sm">
                <button onClick={() => handleViewModeChange('compact')} className={`px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === 'compact' ? 'bg-[#eb4a4a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} title="Compact view"><Minimize2 className="w-4 h-4" /></button>
                <button onClick={() => handleViewModeChange('comfortable')} className={`px-3 py-1.5 text-sm font-medium border-x border-gray-200 transition-colors ${viewMode === 'comfortable' ? 'bg-[#eb4a4a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} title="Comfortable view">Comfortable</button>
                <button onClick={() => handleViewModeChange('auto')} className={`px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === 'auto' ? 'bg-[#eb4a4a] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`} title="Auto view"><Maximize2 className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {isManager && collectionRoot === 'mitigation' && (
            <button onClick={handleExportFirestoreDataAsCSV} disabled={saving} className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">Export to CSV</button>
          )}

          {isManager && collectionRoot === 'data' && (
            <>
              <button onClick={handleExportFirestoreData} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                <Database className="w-4 h-4" />
                Export Data (JSON)
              </button>

              <button onClick={handleExportFirestoreDataAsCSV} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                <Database className="w-4 h-4" />
                Export Data (CSV)
              </button>
            </>
          )}

          {/* ZONA DE EDICIÓN: Solo visible en Edit Mode */}
          {isEditMode && (
            <div className="flex items-center gap-3 pl-4 border-l border-gray-200 ml-1 animate-in fade-in slide-in-from-right-4">

              {/* Add Row - VERDE */}
              {clientId !== 'all' && (
                <button
                  onClick={handleAddRow}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Add Row
                </button>
              )}

              {/* Add Client - VERDE */}
              <button
                onClick={() => setIsAddClientModalOpen(true)}
                className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 shadow-sm transition-all"
              >
                <Plus className="w-4 h-4" />
                Add Client
              </button>

              {/* Delete - ROJO (Se mantiene, pero dentro de la zona de edición) */}
              {selectedRows.length > 0 && (
                <button
                  onClick={() => setIsDeleteModalOpen(true)}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-[#eb4a4a] rounded-md hover:bg-[#d93a3a] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete ({selectedRows.length})
                </button>
              )}
            </div>
          )}

          {/* Recover Button - Visible solo si showDeleted es true y hay filas eliminadas seleccionadas */}
          {showDeleted && selectedRows.some(row => row.rowStatus === 'Eliminated') && (
            <button
              onClick={handleRecoverRows}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-orange-500 rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm ml-3"
            >
              <RotateCcw className="w-4 h-4" />
              Recover ({selectedRows.filter(r => r.rowStatus === 'Eliminated').length})
            </button>
          )}

        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 ag-theme-alpine">
        <AgGridReact
          ref={gridRef}
          rowData={data}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          onCellValueChanged={onCellValueChanged}
          onSelectionChanged={onSelectionChanged}
          animateRows={true}
          enableCellChangeFlash={true}
          reactiveCustomComponents={true}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          stopEditingWhenCellsLoseFocus={false}
          rowHeight={viewMode === 'compact' ? 35 : 42}
          headerHeight={48}
          getRowClass={getRowClass}
          popupParent={document.body}
        />
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#eb4a4a]" />Confirm Deletion</h3>
              <button onClick={() => setIsDeleteModalOpen(false)} className="text-gray-400 hover:text-gray-500 transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <p className="text-gray-600">Are you sure you want to delete <span className="font-bold text-gray-900">{selectedRows.length}</span> selected action{selectedRows.length !== 1 ? 's' : ''}?</p>
              <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
              {selectedRows.length > 0 && (
                <div className="mt-4 bg-gray-50 rounded-md p-3 text-sm border border-gray-100">
                  <ul className="space-y-1">
                    {selectedRows.slice(0, 3).map(row => (
                      <li key={row.id} className="text-gray-700 truncate">• {row.vcpConcept || 'Untitled Action'} (Row {row.rowNumber})</li>
                    ))}
                    {selectedRows.length > 3 && <li className="text-gray-500 italic pl-2">...and {selectedRows.length - 3} more</li>}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-4 bg-gray-50 border-t">
              <button onClick={() => setIsDeleteModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#eb4a4a]" disabled={saving}>Cancel</button>
              <button onClick={handleDeleteRows} className="px-4 py-2 text-sm font-medium text-white bg-[#eb4a4a] rounded-md hover:bg-[#d93a3a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center gap-2" disabled={saving}>{saving ? <><Loader2 className="w-4 h-4 animate-spin" />Deleting...</> : <><Trash2 className="w-4 h-4" />Delete</>}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Client Modal */}
      <AddClientModal
        isOpen={isAddClientModalOpen}
        onClose={() => setIsAddClientModalOpen(false)}
        countryStructureId={countryId}
        countrySapId={countryIsoCode || countryId}
        onClientAdded={handleClientAdded}
      />

      {/* Move Actions Modal */}
      <MoveActionsModal
        isOpen={isMoveModalOpen}
        onClose={() => setIsMoveModalOpen(false)}
        selectedActions={selectedRows}
        onConfirmMove={handleConfirmMoveActions}
        clients={clients}
      />

      {/* Rename Client Modal */}
      <RenameClientModal
        isOpen={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        currentClientName={clients.find(c => c.id === clientId)?.name || ''}
        onConfirmRename={handleConfirmRenameClient}
      />
    </div>
  );
}
